import { useRef, useState } from "react";
import { Link } from "wouter";
import { ArrowRight, Plus } from "lucide-react";
import type { StandoutScoreData } from "@/lib/api";
import { ApexScorePresentation } from "./ApexScorePresentation";
import { APEX_FACTORS, apexTierLabel } from "@/components/apex/apex-factors";

export interface ApexExampleData {
  verifiedAt: string;
  available: boolean;
  name: string;
  path: string;
  image: { small: string; medium: string; large: string; full: string; alt: string };
  score: StandoutScoreData;
}

function formatDate(iso: string) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

function Unavailable() {
  return (
    <div className="rounded-2xl border border-brand-editorial-main/15 bg-brand-tint p-8 sm:p-10" data-testid="apex-example-unavailable">
      <p className="ax-kicker text-brand-light">Example unavailable</p>
      <p className="mt-3 max-w-xl leading-relaxed text-brand-dark-ink/80">
        We could not show the published score example right now. Where APEX is available on a listing, open it to explore the seven factors and their written reasoning.
      </p>
      <Link href="/search" className="ax-link mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-editorial-main underline underline-offset-4" data-testid="link-example-unavailable-search">
        Browse cars to see scores in context <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </div>
  );
}

export function ApexExample({ example }: { example: ApexExampleData | null }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [firstOpen, setFirstOpen] = useState(false);
  const firstFactor = useRef<HTMLDetailsElement>(null);
  if (!example || !example.available || imgFailed || !example.score?.total_score) return <Unavailable />;
  const { score, image } = example;
  const tier = apexTierLabel(score.total_score);

  return (
    <div className="grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:items-start" data-testid="apex-example">
      <figure>
        <div className="ax-photo aspect-[4/3] bg-brand-tint">
          <img
            src={image.large}
            srcSet={`${image.small} 400w, ${image.medium} 800w, ${image.large} 1200w`}
            sizes="(min-width: 1024px) 600px, 100vw"
            alt={image.alt}
            width={1200}
            height={675}
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
            data-testid="img-apex-example"
          />
        </div>
        <figcaption className="mt-4 text-sm leading-relaxed text-brand-dark-ink/65">
          Snapshot of a public listing, recorded {formatDate(example.verifiedAt)}. Shown to explain how a score reads; it does not indicate current availability, price or condition.
        </figcaption>
      </figure>

      <div>
        <p className="ax-kicker text-brand-light">Worked example</p>
        <h3 className="ax-display mt-3 text-3xl text-brand-editorial-main" data-testid="text-example-name">{example.name}</h3>
        <p className="mt-2 ax-tabular text-brand-dark-ink/75">
          <span className="text-2xl font-semibold text-brand-editorial-main" data-testid="text-example-score">{score.total_score}</span> / 100
          {tier && <> &middot; {tier} tier</>}
        </p>
        {score.one_liner && <p className="mt-4 text-lg leading-relaxed text-brand-dark-ink/80">{score.one_liner}</p>}

        <div className="mt-6">
          <p className="mb-2 text-sm text-brand-dark-ink/65">This is the score panel shown on listing pages. Select it to explore the breakdown, or open each factor below. With reduced motion enabled, the panel opens the first factor below without animation.</p>
          <ApexScorePresentation score={score} onInlineBreakdown={() => {
            setFirstOpen(true);
            requestAnimationFrame(() => firstFactor.current?.querySelector("summary")?.focus());
          }} />
        </div>

        <div className="mt-8">
          <h4 className="text-sm font-semibold text-brand-editorial-main">Factor by factor</h4>
          <div className="mt-3 border-t border-brand-editorial-main/15">
            {APEX_FACTORS.map((f, index) => {
              const raw = score[f.key] as number | null;
              if (typeof raw !== "number") return null;
              const val = Math.min(raw, f.max);
              const why = score[f.justKey] as string | null;
              return (
                <details key={f.key} ref={index === 0 ? firstFactor : undefined}
                  open={index === 0 ? firstOpen : undefined}
                  onToggle={index === 0 ? event => setFirstOpen(event.currentTarget.open) : undefined}
                  className="ax-details border-b border-brand-editorial-main/15" data-testid={`details-example-${f.key}`}>
                  <summary className="flex items-center gap-4 py-4">
                    <span className="flex-1">
                      <span className="flex items-baseline justify-between gap-3">
                        <span className="font-medium text-brand-editorial-main">{f.label}</span>
                        <span className="ax-tabular text-sm font-semibold">{val}<span className="text-brand-dark-ink/50">/{f.max}</span></span>
                      </span>
                      <span className="ax-bar mt-2 block" aria-hidden><span style={{ width: `${(val / f.max) * 100}%` }} /></span>
                    </span>
                    <Plus className="ax-icon h-4 w-4 shrink-0 text-brand-editorial-main" aria-hidden />
                  </summary>
                  {why && <p className="pb-5 leading-relaxed text-brand-dark-ink/75">{why}</p>}
                </details>
              );
            })}
          </div>
        </div>

        <Link href={example.path} className="ax-link mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-editorial-main underline underline-offset-4" data-testid="link-example-listing">
          View this listing <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </div>
  );
}
